/**
 * Recursos (Resource Management) API Service
 *
 * Endpoints for managing project resources, financing plans, investments,
 * and financial transactions for film production projects.
 *
 * Pattern: Follows existing service patterns from auth.ts, budget.ts, etc.
 */

import { api } from "@/lib/api/axios";
import { TransactionFilter } from "@/types/recursos-dashboard.interface";
import {
  FinancingPlanItem,
  InvestmentContractItem,
  ResourceComposition
} from "@/types/gestao-recursos.interface";
import { DashboardData } from "@/interfaces/dashboard.interface";
import { ProjectResources } from "@/interfaces/project.interface";
import { ApiResponse } from "@/interfaces/api-response.interface";

const RECURSOS_API_PATH = '/recursos';

/**
 * Recursos Service
 * All endpoints for resource management functionality
 */
export const recursosService = {
  /**
   * Get dashboard overview data for a specific project
   * Includes balance summary, transactions, and statistics
   *
   * @param projectId - Unique project identifier
   * @returns Dashboard data with balance cards, transactions, charts
   */
  getDashboardData: (projectId: string) => {
    return api.get<ApiResponse<DashboardData>>(`${RECURSOS_API_PATH}/dashboard/${projectId}`);
  },

  /**
   * Get detailed resource information for a project
   * Includes budget breakdown, financing plan, investments
   *
   * @param projectId - Unique project identifier
   * @returns Complete resource management data
   */
  getProjectResources: (projectId: string) => {
    return api.get<ApiResponse<ProjectResources>>(`${RECURSOS_API_PATH}/${projectId}`);
  },

  /**
   * Get list of available projects for the current user
   * Used in project selector dropdown
   *
   * @returns Array of projects with id, name, salicNumber
   */
  getAvailableProjects: () => {
    return api.get<{ data: unknown }>(`${RECURSOS_API_PATH}/projects`);
  },

  /**
   * Get transactions for a specific project with optional filters
   * Supports filtering by status, date range, and value range
   *
   * @param projectId - Unique project identifier
   * @param filters - Optional transaction filters
   * @returns Paginated transaction list
   */
  getTransactions: (projectId: string, filters?: TransactionFilter) => {
    return api.get<{ data: unknown }>(`${RECURSOS_API_PATH}/${projectId}/transactions`, {
      params: filters
    });
  },

  /**
   * Get monthly statistics for charts
   * Returns completed vs pending data for bar charts
   *
   * @param projectId - Unique project identifier
   * @param months - Number of months to retrieve (default: 7)
   * @returns Array of monthly statistics
   */
  getMonthlyStatistics: (projectId: string, months: number = 7) => {
    return api.get<{ data: unknown }>(`${RECURSOS_API_PATH}/${projectId}/statistics/monthly`, {
      params: { months }
    });
  },

  /**
   * Get compact chart data for dashboard sidebar
   * Returns data for 3 compact charts (Balance, Investment, Resources)
   *
   * @param projectId - Unique project identifier
   * @returns Object with 3 compact chart datasets
   */
  getCompactCharts: (projectId: string) => {
    return api.get<{ data: unknown }>(`${RECURSOS_API_PATH}/${projectId}/charts/compact`);
  },

  /**
   * Update financing plan for a project
   * Modifies funding sources and amounts
   *
   * @param projectId - Unique project identifier
   * @param data - Financing plan data
   * @returns Updated financing plan
   */
  updateFinancingPlan: (projectId: string, data: FinancingPlanItem[]) => {
    return api.put(`${RECURSOS_API_PATH}/${projectId}/financing-plan`, { items: data });
  },

  /**
   * Update investment contracts for a project
   * Modifies investor/sponsor information and amounts
   *
   * @param projectId - Unique project identifier
   * @param data - Investment contract data
   * @returns Updated investment contracts
   */
  updateInvestmentContracts: (projectId: string, data: InvestmentContractItem[]) => {
    return api.put(`${RECURSOS_API_PATH}/${projectId}/investment-contracts`, { items: data });
  },

  /**
   * Update complete resource composition for a project
   * Updates budget summary, financing plan, and investment data
   *
   * @param projectId - Unique project identifier
   * @param data - Complete resource composition data
   * @returns Updated resource composition
   */
  updateResourceComposition: (projectId: string, data: ResourceComposition) => {
    return api.put(`${RECURSOS_API_PATH}/${projectId}/composition`, data);
  },
};
